import { useState, useRef, useEffect } from 'react';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useSpring,
  useInView,
} from 'framer-motion';
import { FeaturedStackingShowcase } from './components/FeaturedStackingShowcase';

// ─── DATA ────────────────────────────────────────────────────────────────────

const NAV_LINKS = ['Home', 'About', 'Projects', 'Blogs'];

function easeOutQuart(t) {
  return 1 - (1 - t) ** 4;
}

/** Counts from 0 to target when `inView` becomes true. Optional stagger `delayMs` and `suffix` (e.g. '+'). */
function useCountUp(target, duration, inView, { delayMs = 0, suffix = '' } = {}) {
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!inView || startedRef.current) return undefined;

    const timeoutId = window.setTimeout(() => {
      startedRef.current = true;
      const start = performance.now();

      const tick = (now) => {
        const elapsed = now - start;
        const t = Math.min(1, elapsed / duration);
        const eased = easeOutQuart(t);
        setValue(eased * target);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          setValue(target);
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    }, delayMs);

    return () => {
      window.clearTimeout(timeoutId);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [inView, target, duration, delayMs]);

  const rounded = Math.round(value);
  return suffix ? `${rounded}${suffix}` : String(rounded);
}

function AboutStatBlock({ target, label, suffix = '', motionDelay, countDelayMs, inView }) {
  const display = useCountUp(target, 1800, inView, { delayMs: countDelayMs, suffix });
  return (
    <motion.div
      role="listitem"
      initial={{ y: 24, opacity: 0 }}
      animate={inView ? { y: 0, opacity: 1 } : {}}
      transition={{ duration: 0.6, delay: motionDelay, ease: 'easeOut' }}
    >
      <div className="about-stat-num">{display}</div>
      <div className="about-stat-label">{label}</div>
    </motion.div>
  );
}

const SERVICES = [
  {
    id: 1,
    label: 'Hospitality',
    desc: 'Portfolio includes strategically located 16+ hotels across Makkah, Madinah & Jeddah, delivering comfort, elegance, and spiritually enriching stays tailored for pilgrims and travelers.',
    img: '/HotelService.jpeg',
  },
  {
    id: 2,
    label: 'Transportation',
    desc: 'With a fleet of 60+ buses, we provide safe, reliable, and fully guided travel solutions for pilgrims across the Kingdom, ensuring comfort and seamless mobility for every journey.',
    img: '/848A8036.jpg',
  },
  {
    id: 3,
    label: 'Hajj & Umrah Services',
    desc: 'We deliver complete Hajj & Umrah services with care and reliability, ensuring a seamless, well-guided, and spiritually enriching journey for every pilgrim.',
    img: '/Mukhtara_Banner3%201.png',
  },
  {
    id: 4,
    label: 'Mukhtara Air',
    desc: 'Introducing Mukhtara Air, our exclusive airline service launching in Q2 2026, connecting Far East Asia and Africa—further strengthening Al Mokhtara position as a fully integrated and uniquely comprehensive hospitality and travel provider.',
    img: '/newFlight2.jpg',
  },
  {
    id: 5,
    label: 'Catering',
    desc: 'A specialized hospitality service dedicated to high-quality catering and food supply solutions in Madinah for pilgrims, hotels, and hospitality establishments.',
    img: '/wFw9B.jpg',
  },
  {
    id: 6,
    label: 'Travel & Tourism',
    desc: 'An online platform offering Umrah packages, hotel bookings, and transport services for pilgrims.',
    img: '/tyHHe.jpg',
  },
  {
    id: 7,
    label: 'Digital Platforms',
    desc: 'Zowar & UmrahMall represent our digital backbone: curated journeys, transparent pricing, and dependable assistance from first inquiry to arrival. The platforms are built for clarity and speed, with secure flows, guided steps, and responsive support so users can plan, book, and travel with confidence.',
    img: '/abc.png',
  },
];

const FEATURED_PROJECTS = [
  {
    id: 'fp-1',
    category: 'Hospitality',
    title: 'MUKHTARA ELITE HOTEL',
    desc: 'A premium hospitality destination designed to deliver comfort, elegance, and exceptional guest experiences. Blending modern architecture with thoughtful amenities for today’s travelers.',
    img: '/SHOTS%2009_page-0006.jpg',
    badge: 'Opening 2027',
    imageFit: 'cover',
    imagePosition: 'center 74%',
  },
  {
    id: 'fp-2',
    category: 'Commercial',
    title: 'MUKHTARA MALL',
    desc: 'A landmark retail destination near Al Haram, bringing together premium brands, curated dining, and a contemporary shopping experience.',
    img: '/up2.png',
    badge: 'Opening June 2026',
    imageFit: 'cover',
    imagePosition: 'center 70%',
  },
  {
    id: 'fp-3',
    category: 'Commercial',
    title: 'MOKHTARA BOULEVARD',
    desc: 'A vibrant commercial hub designed for business and lifestyle, featuring modern architecture, retail spaces, and dynamic urban experiences.',
    img: '/up1.png',
    badge: 'Opening June 2026',
    imageFit: 'cover',
    imagePosition: 'center 72%',
  },
];

const TESTIMONIALS = [
  {
    id: 't1',
    type: 'review',
    quote: "Managing a growing portfolio of premium hotels across key cities, delivering exceptional comfort, service, and experiences for pilgrims and travelers.",
    name: '17+ Hotels Across Saudi Arabia',
    role: 'Expanding Hospitality Excellence',
    // avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=faces',
  },
  {
    id: 't2',
    type: 'review',
    quote: 'Ensuring premium guest experiences through efficient operations, high standards, and attention to detail.',
    name: '4,000+ Rooms Managed',
    role: 'Hospitality at Scale',
    // avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&h=80&fit=crop&crop=faces',
  },
  {
    id: 't3',
    type: 'stat-dark',
    label: "I've worked with 50+ happy clients",
    stat: '98%',
    statCaption: 'Satisfaction Rate',
  },
  {
    id: 't4',
    type: 'stat-accent',
    label: 'My work helped clients grow their revenue by 200%',
    stat: '200%',
    statCaption: 'Growth',
  },
  {
    id: 't5',
    type: 'review',
    quote: 'Delivering exceptional experiences in the most important destinations for pilgrims and travelers..',
    name: 'Madinah • Makkah • Jeddah',
    role: 'Prime Hospitality Locations',
    // avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&h=80&fit=crop&crop=faces',
  },
  {
    id: 't6',
    type: 'review',
    quote: 'Introducing a premium airline experience focused on comfort, reliability, and global connectivity.',
    name: 'Mukhtara Air',
    role: 'Aviation Expansion',
    // avatar: 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=80&h=80&fit=crop&crop=faces',
  },
];

const FAQ_ITEMS = [
  {
    id: 1,
    q: 'What does Al Mokhtara Group specialize in?',
    a: 'Celebrating 22 Years of Trusted Hospitality and Exceptional Service at Al Mokhtara Group.',
  },
  {
    id: 2,
    q: 'How does the design process work?',
    a: 'We start with discovery, then wireframes, visual direction, design iterations, and final handoff with clear implementation guidelines.',
  },
  {
    id: 3,
    q: 'How long does a project usually take?',
    a: 'Typical timelines are 2-6 weeks based on scope, number of pages, revisions, and content readiness.',
  },
  {
    id: 4,
    q: 'What do I need to provide before starting a project?',
    a: 'Any existing brand assets, goals, references, target audience details, and preferred content structure are helpful to begin quickly.',
  },
  {
    id: 5,
    q: 'Do you offer revisions?',
    a: 'Yes, every project includes revision rounds to ensure the final result matches your vision and business goals.',
  },
  {
    id: 6,
    q: 'How do I get started?',
    a: 'Send a short brief with your goals and timeline, and we will schedule a kickoff call to align on scope and milestones.',
  },
];

const BLOG_POSTS = [
  {
    id: 'b1',
    tag: 'International Recognition',
    title: 'Philippine Hajj Delegation',
    excerpt: 'Honored by the Philippine Hajj Delegation for outstanding hospitality services during Hajj 1446 AH (2025), recognizing excellence in serving international pilgrims.',
    date: 'Hajj 1446 AH (2025)',
    img: '/achievement2025.jpg',
  },
  {
    id: 'b2',
    tag: 'Service Excellence',
    title: 'Official Hajj Service Commendation',
    excerpt: 'Awarded an official certificate of excellence for outstanding hospitality and pilgrim services during Hajj 1446 AH, reflecting a strong commitment to quality and care.',
    date: 'Hajj 1446 AH',
    img: '/achievement2024.jpg',
    fitMode: 'contain',
  },
  {
    id: 'b3',
    tag: 'Chairman’s Honour',
    title: 'Khartoum Hajj Delegation',
    excerpt: 'Khartoum Hajj Delegation honored Mr. Sami Marzoug Al-Mutrafi, Chairman of Manazil Al Mokhtara, for his dedicated service and commitment to Hajj 1446 AH pilgrims.',
    date: 'Hajj 1446 AH',
    img: '/achievement3.jpg',
    
  },
];

const BRAND_HIGHLIGHTS = [
  { name: 'Al Mokhtara Group', image: '/Company/logo-6.png', link: 'https://mokhtaragroup.com' },
  { name: 'Op Craft', image: '/Company/op_craft_indus.png', link: 'https://mokhtaragroup.com' },
  { name: 'Hasoob Technology', image: '/Company/hasoob_tech.png', link: 'https://www.hasob.com.sa' },
  { name: 'Hasoob Umrah', image: '/Company/hasoob_umrah_services.png', link: 'https://www.hasob.com.sa' },
  { name: 'Zowar', image: '/Company/zowar_almukhtara.png', link: 'https://www.zowar.com.sa' },
  { name: 'ODST', image: '/Company/Odst_logo.png', link: 'https://odst.sa' },
  { name: 'Takaful Taiba', image: '/Company/Tayyaba_logo.png', link: 'https://mokhtaragroup.com' },
  { name: 'Mukhtara Air', image: '/Company/al_mukhtara_air.png', link: 'https://www.mukhtaraair.com' },
  { name: 'Ozone Company', image: '/Company/digi_o_zone.png', link: 'https://mokhtaragroup.com' },
  {
    name: 'Zowar Al Mokhtara for Catering & Food Supplies – Madinah',
    image: '/Company/zowar_almukhtara.png',
    link: 'https://mokhtaragroup.com',
  },
  { name: 'UmrahMall', image: '/Company/umrah_mall.png', link: 'https://www.umrahmall.com' },
  { name: 'Visit', image: '/Company/visit.png', link: 'https://www.visit.com.sa' },
];

// ─── STYLES ──────────────────────────────────────────────────────────────────

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:     #f5f3ef;
    --ink:    #111;
    --accent: #3d4cf5;
    --muted:  #888;
    --card:   #e8e3db;
    --border: #ddd9d2;
  }

  .dark {
    --bg:     #111111;
    --ink:    #f0ede7;
    --accent: #5c6aff;
    --muted:  #666;
    --card:   #1c1c1c;
    --border: #2a2a2a;
  }

  .portfolio {
    background: var(--bg);
    color: var(--ink);
    font-family: 'DM Sans', system-ui, sans-serif;
    font-weight: 400;
    min-height: 100vh;
    /* hidden breaks position:sticky for descendants (stack pins to wrong box / scrolls away). clip avoids horizontal bleed without that bug. */
    overflow-x: clip;
    overflow-y: visible;
    transition: background 0.35s ease, color 0.35s ease;
    position: relative;
  }
  .portfolio > * {
    position: relative;
    z-index: 1;
  }
  .bg-blue-balls {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
  }
  .bg-blue-ball {
    position: absolute;
    border-radius: 50%;
    background: radial-gradient(circle at 32% 30%, #8da0ff 0%, #6f85ff 38%, #4f68f3 72%, #3f57e2 100%);
    opacity: 0.28;
    filter: blur(0.2px);
    animation: blueDrift var(--dur, 14s) ease-in-out infinite, bluePulse var(--pulse, 5s) ease-in-out infinite;
    will-change: transform, opacity;
  }
  .bg-blue-ball:nth-child(1) { width: 8px; height: 8px; left: 8%; top: 18%; --dur: 13s; --pulse: 4.5s; animation-delay: -1s; }
  .bg-blue-ball:nth-child(2) { width: 10px; height: 10px; left: 22%; top: 64%; --dur: 15s; --pulse: 5.2s; animation-delay: -2.2s; }
  .bg-blue-ball:nth-child(3) { width: 7px; height: 7px; left: 36%; top: 42%; --dur: 12s; --pulse: 4.2s; animation-delay: -0.6s; }
  .bg-blue-ball:nth-child(4) { width: 12px; height: 12px; left: 51%; top: 10%; --dur: 16s; --pulse: 5.8s; animation-delay: -3.1s; }
  .bg-blue-ball:nth-child(5) { width: 9px; height: 9px; left: 63%; top: 72%; --dur: 14s; --pulse: 4.8s; animation-delay: -1.7s; }
  .bg-blue-ball:nth-child(6) { width: 7px; height: 7px; left: 78%; top: 30%; --dur: 11.5s; --pulse: 4.1s; animation-delay: -2.8s; }
  .bg-blue-ball:nth-child(7) { width: 11px; height: 11px; left: 90%; top: 56%; --dur: 17s; --pulse: 6.1s; animation-delay: -0.9s; }
  .bg-blue-ball:nth-child(8) { width: 8px; height: 8px; left: 14%; top: 86%; --dur: 13.8s; --pulse: 5s; animation-delay: -2.4s; }
  @keyframes blueDrift {
    0% { transform: translate3d(0, 0, 0); }
    25% { transform: translate3d(6px, -8px, 0); }
    50% { transform: translate3d(-4px, -14px, 0); }
    75% { transform: translate3d(-8px, -6px, 0); }
    100% { transform: translate3d(0, 0, 0); }
  }
  @keyframes bluePulse {
    0%, 100% { opacity: 0.2; }
    50% { opacity: 0.36; }
  }

  /* ── NAV ── */
  .nav-outer {
    position: sticky;
    top: 16px;
    z-index: 100;
    display: flex;
    justify-content: center;
    padding: 0 24px;
    pointer-events: none;
  }
  .nav-pill {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 8px 10px;
    border-radius: 999px;
    background: var(--bg);
    border: 1px solid var(--border);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: 0 2px 24px rgba(0,0,0,0.08);
    pointer-events: all;
    transition: background 0.35s ease, border-color 0.35s ease;
  }
  .nav-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    margin-right: 10px;
  }
  .nav-link {
    background: none;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: var(--muted);
    padding: 6px 14px;
    border-radius: 999px;
    transition: color 0.2s, background 0.2s;
  }
  .nav-link:hover { color: var(--ink); background: var(--card); }
  .nav-sep {
    width: 1px;
    height: 18px;
    background: var(--border);
    margin: 0 6px;
    flex-shrink: 0;
  }
  .nav-cta {
    background: var(--ink);
    color: var(--bg);
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    padding: 8px 18px;
    border-radius: 999px;
    margin-left: 4px;
    transition: opacity 0.2s, background 0.35s, color 0.35s;
  }
  .nav-cta:hover { opacity: 0.82; }

  /* ── HERO ── */
  .hero {
    position: relative;
    padding: 28px 48px 80px;
    display: flex;
    flex-direction: column;
    min-height: calc(100vh - 66px);
    overflow: hidden;
  }
  .hero-label {
    font-size: clamp(34px, 3.2vw, 56px);
    font-weight: 600;
    letter-spacing: 0.06em;
    line-height: 0.92;
    text-transform: uppercase;
    color: var(--muted);
    margin: 0;
  }
  .hero-label--left {
    text-align: left;
  }
  /* Flat grid: row1 = name (col1 only); row2 = DIGITAL | portrait | DESIGNER (same line, baseline-aligned); row3 = bio */
  .hero-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    grid-template-rows: auto auto auto;
    column-gap: clamp(20px, 4vw, 52px);
    row-gap: 10px;
    align-items: start;
    flex: 1;
    width: 100%;
    max-width: min(1280px, 100%);
    margin-inline: auto;
    perspective: 1000px;
  }
  .hero-name {
    grid-column: 1;
    grid-row: 1;
    justify-self: end;
    align-self: end;
    max-width: min(100%, 22ch);
    min-width: 0;
    padding-right: 8px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .hero-digital {
    grid-column: 1;
    grid-row: 2;
    justify-self: end;
    align-self: start;
    min-width: 0;
  }
  .hero-card {
    grid-column: 2;
    grid-row: 1 / -1;
    align-self: center;
    justify-self: center;
    transform-style: preserve-3d;
    will-change: transform;
  }
  .hero-card-loop {
    position: relative;
    transform-style: preserve-3d;
    will-change: transform;
    z-index: 1;
  }
  .hero-word {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(52px, 9vw, 132px);
    line-height: 0.9;
    color: var(--ink);
    user-select: none;
    letter-spacing: 0.01em;
  }
  /* DESIGNER + tagline: narrow column, text right-aligned like reference */
  .hero-right-stack {
    grid-column: 3;
    grid-row: 2 / span 2;
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
    justify-self: start;
    align-self: start;
    max-width: 100%;
    min-width: 0;
  }
  .hero-sub-wrap {
    text-align: right;
    max-width: min(280px, 100%);
    margin: 0;
  }
  .hero-sub-desc {
    font-size: clamp(16px, 1.15vw, 22px);
    font-weight: 400;
    color: var(--muted);
    line-height: 1.45;
    margin: 0;
  }

  /* ── PHOTO CARD ── */
  .card-wrap {
    flex-shrink: 0;
    width: clamp(210px, 20vw, 300px);
    position: relative;
  }
  .photo-card {
    width: 100%;
    aspect-ratio: 3 / 4.2;
    border-radius: 24px;
    overflow: hidden;
    position: relative;
    background: var(--card);
    isolation: isolate;
    will-change: transform;
    z-index: 1;
    transform: translateZ(0);
  }
  .photo-card > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  /*
   * Center of the circle sits on the image’s bottom-left corner: exactly one quadrant
   * of the disk lies inside the photo (~25% area), the rest sits outside (~75%) — matches common Framer-style badges.
   */
  .wave-btn-shell {
    position: absolute;
    left: 0;
    bottom: 0;
    transform: translate(-18%, 18%) translateZ(36px);
    z-index: 12;
    pointer-events: none;
    overflow: visible;
  }
  .wave-btn {
    position: relative;
    width: clamp(56px, 11vw, 76px);
    height: clamp(56px, 11vw, 76px);
    border-radius: 50%;
    background: var(--accent);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: visible;
    box-shadow: 0 4px 12px rgba(61, 76, 245, 0.22);
  }
  .wave-btn-phase-placeholder {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 4px;
  }
  .wave-btn-hand-svg {
    width: 56%;
    height: 56%;
    max-width: 42px;
    max-height: 42px;
    display: block;
    flex-shrink: 0;
    overflow: visible;
  }
  .hi-text {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(17px, 3.8vw, 24px);
    color: #fff;
    letter-spacing: 0.06em;
    line-height: 1;
  }
  /* ── DARK TOGGLE ── */
  .toggle-wrap {
    position: absolute;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 10px;
    z-index: 10;
    white-space: nowrap;
  }
  .toggle-label { font-size: 12px; color: var(--muted); }
  .toggle-track {
    width: 48px;
    height: 26px;
    border-radius: 999px;
    background: var(--card);
    border: 1px solid var(--border);
    cursor: pointer;
    position: relative;
    flex-shrink: 0;
    transition: background 0.3s, border-color 0.3s;
  }
  .toggle-track.on { background: var(--accent); border-color: var(--accent); }
  .toggle-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.22);
    position: absolute;
    top: 3px;
  }

  /* ── SERVICES ── */
  .services {
    padding: 100px 48px 120px;
    max-width: 1200px;
    margin: 0 auto;
  }
  .svc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: start;
  }
  .svc-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(40px, 5vw, 68px);
    line-height: 1;
    letter-spacing: 0.02em;
    margin-bottom: 20px;
  }
  .svc-sub {
    font-size: 15px;
    font-weight: 300;
    color: var(--muted);
    line-height: 1.7;
    max-width: 380px;
    margin-bottom: 48px;
  }

  /* ── ACCORDION ── */
  .accordion { border-top: 1px solid var(--border); }
  .acc-item { border-bottom: 1px solid var(--border); }
  .acc-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 18px 0;
    cursor: pointer;
    user-select: none;
    background: none;
    border: none;
    width: 100%;
    text-align: left;
  }
  .acc-num {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 400;
    color: var(--muted);
    width: 22px;
    flex-shrink: 0;
    transition: color 0.2s;
  }
  .acc-num.on { color: var(--accent); }
  .acc-label {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 27px;
    letter-spacing: 0.04em;
    color: var(--ink);
    flex: 1;
    transition: color 0.2s;
  }
  .acc-label.on { color: var(--accent); }
  .acc-chevron {
    font-size: 14px;
    color: var(--muted);
    display: flex;
    align-items: center;
    line-height: 1;
  }
  .acc-body { overflow: hidden; }
  .acc-desc {
    font-size: 14px;
    font-weight: 300;
    color: var(--muted);
    line-height: 1.7;
    padding: 2px 0 20px 32px;
    max-width: 420px;
  }

  /* ── RIGHT IMAGE ── */
  .svc-right { position: sticky; top: 120px; }
  .right-img {
    width: 100%;
    aspect-ratio: 4 / 5;
    object-fit: cover;
    border-radius: 24px;
    display: block;
  }

  /* ── FLOATING BADGE ── */
  .f-badge {
    position: fixed;
    bottom: 28px;
    right: 28px;
    z-index: 200;
    background: #ffffff;
    border-radius: 16px;
    padding: 10px 10px 9px;
    box-shadow: 0 8px 40px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.05);
    width: 152px;
  }
  .dark .f-badge { background: #1e1e1e; }
  .f-badge-tag {
    font-size: 12px;
    font-weight: 600;
    color: var(--accent);
    letter-spacing: 0.04em;
    text-transform: none;
    margin-bottom: 7px;
    line-height: 1.25;
    min-height: 28px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
  }
  .f-badge-thumb {
    width: 100%;
    height: 52px;
    border-radius: 8px;
    background: #ffffff;
    margin-bottom: 8px;
    overflow: hidden;
    border: 1px solid var(--border);
  }
  .f-badge-thumb img { width: 100%; height: 100%; object-fit: contain; display: block; padding: 6px; }
  .f-badge-actions { display: flex; gap: 6px; }
  .f-btn {
    flex: 1;
    padding: 6px 0;
    border-radius: 7px;
    border: 1px solid var(--border);
    background: none;
    font-family: 'DM Sans', sans-serif;
    font-size: 11px;
    font-weight: 400;
    color: var(--ink);
    cursor: pointer;
    transition: background 0.2s;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .f-btn:hover { background: var(--card); }
  .f-btn.primary {
    background: var(--ink);
    color: var(--bg);
    border-color: var(--ink);
  }
  .f-btn.primary:hover { opacity: 0.8; }

  /* ── CURSOR FOLLOWER ── */
  .cursor-img {
    position: fixed;
    top: 0;
    left: 0;
    width: 175px;
    height: 118px;
    border-radius: 18px;
    object-fit: cover;
    pointer-events: none;
    z-index: 999;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 820px) {
    .hero { padding: 16px 20px 72px; }
    .hero-row {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }
    .hero-name,
    .hero-digital,
    .hero-card,
    .hero-right-stack {
      grid-column: unset;
      grid-row: unset;
      justify-self: unset;
      align-self: unset;
    }
    .hero-label--left {
      text-align: center;
    }
    .hero-name {
      width: 100%;
      text-align: center;
    }
    .hero-digital {
      align-self: center;
    }
    .hero-right-stack {
      align-items: center;
      width: 100%;
    }
    .card-wrap {
      width: clamp(160px, 42vw, 240px);
    }
    .hero-sub-wrap {
      text-align: center;
      max-width: 320px;
      margin-inline: auto;
    }
    .hero-sub-desc {
      font-size: clamp(15px, 4.4vw, 20px);
      margin-inline: auto;
    }
    .hero-word { font-size: clamp(44px, 14vw, 88px); }
    .services { padding: 60px 24px 80px; }
    .svc-grid { grid-template-columns: 1fr; gap: 40px; }
    .svc-right { display: none; }
    .f-badge { display: none; }
    .bg-blue-ball { opacity: 0.22; }
  }

  /* ── ABOUT ── */
  .about {
    max-width: 1280px;
    margin: 0 auto;
    padding: 100px 60px;
  }
  .about-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: center;
  }
  .about-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(52px, 6vw, 80px);
    line-height: 0.95;
    color: var(--ink);
  }
  .about-desc {
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    font-size: 16px;
    line-height: 1.7;
    color: var(--muted);
    max-width: 440px;
    margin-top: 22px;
  }
  .about-desc-second {
    margin-top: 14px;
  }
  .about-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(120px, 1fr));
    gap: 28px;
    margin-top: 48px;
    max-width: 520px;
  }
  .about-stats > [role='listitem'] {
    min-width: 0;
  }
  .about-stat-num {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(52px, 5vw, 72px);
    line-height: 0.95;
    color: var(--accent);
  }
  .about-stat-label {
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: var(--ink);
    margin-top: 6px;
    line-height: 1.35;
  }
  .about-contact {
    display: flex;
    flex-wrap: wrap;
    gap: 60px;
    margin-top: 48px;
  }
  .about-contact-label {
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: var(--ink);
  }
  .about-contact-val {
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    font-size: 15px;
    color: var(--muted);
    margin-top: 4px;
  }
  .about-social {
    display: flex;
    flex-direction: row;
    gap: 12px;
    margin-top: 36px;
  }
  .about-soc-btn {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 1.5px solid var(--border);
    background: transparent;
    color: var(--ink);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  }
  .about-soc-btn:hover {
    background: var(--ink);
    color: var(--bg);
  }
  .about-soc-btn svg { display: block; }
  .about-story-btn {
    margin-top: 40px;
    border: 2px solid var(--ink);
    border-radius: 999px;
    padding: 14px 36px;
    font-family: 'Bebas Neue', sans-serif;
    font-size: 18px;
    letter-spacing: 0.08em;
    background: transparent;
    color: var(--ink);
    cursor: pointer;
    transition: background 0.22s ease, color 0.22s ease;
  }
  .about-story-btn:hover {
    background: var(--ink);
    color: var(--bg);
  }
  .about-photo-outer {
    position: relative;
    width: 100%;
    transform-origin: center center;
  }
  .about-photo-card {
    position: relative;
    width: 100%;
    aspect-ratio: 3 / 4;
    border-radius: 28px;
    overflow: hidden;
    box-shadow: 0 32px 80px rgba(0,0,0,0.13);
    background: var(--card);
  }
  .about-photo-card img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .about-pulse-dot {
    position: absolute;
    left: -18px;
    top: calc(50% - 6.5px);
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: var(--accent);
    pointer-events: none;
  }

  @media (max-width: 900px) {
    .about {
      padding: 60px 24px;
    }
    .about-grid {
      grid-template-columns: 1fr;
      gap: 48px;
    }
    .about-stats {
      grid-template-columns: repeat(3, minmax(96px, 1fr));
      gap: 18px;
      max-width: 100%;
    }
    .about-stat-label {
      font-size: 13px;
    }
  }

  /* ── MARQUEE ── */
  .marquee-outer {
    overflow: hidden;
    padding: 22px 0;
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
    background: var(--card);
  }
  .marquee-track {
    display: flex;
    width: max-content;
  }
  .marquee-content {
    display: flex;
    align-items: center;
    gap: 0;
    padding-right: 0;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .marquee-item {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(20px, 2.4vw, 30px);
    letter-spacing: 0.07em;
    color: var(--ink);
    opacity: 0.72;
    padding: 0 20px;
  }
  .marquee-sep {
    color: var(--accent);
    font-size: 16px;
    opacity: 0.9;
    flex-shrink: 0;
  }

  /* ── TESTIMONIALS ── */
  .testimonials {
    padding: 100px 48px;
    max-width: 1280px;
    margin: 0 auto;
  }
  .testi-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 60px;
    gap: 24px;
    flex-wrap: wrap;
  }
  .testi-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(44px, 5.5vw, 72px);
    line-height: 0.95;
    letter-spacing: 0.02em;
    color: var(--ink);
  }
  .testi-sub {
    font-size: 14px;
    font-weight: 300;
    color: var(--muted);
    max-width: 300px;
    line-height: 1.6;
    text-align: right;
  }
  .testi-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 22px;
  }
  .testi-card {
    background: linear-gradient(180deg, color-mix(in srgb, var(--card) 92%, #fff 8%), var(--card));
    border-radius: 22px;
    padding: 28px 26px;
    border: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    box-shadow: 0 10px 26px rgba(16, 24, 40, 0.06);
    transition: transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease;
  }
  .testi-card::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(90deg, #4f68f3, #7b8cff);
    opacity: 0.8;
  }
  .testi-card:hover {
    box-shadow: 0 20px 44px rgba(16, 24, 40, 0.12);
    border-color: color-mix(in srgb, var(--accent) 26%, var(--border));
  }
  .testi-card--dark {
    background: linear-gradient(180deg, #22252a, #1a1e27);
    color: #fff;
    border-color: transparent;
  }
  .testi-card--accent {
    background: linear-gradient(180deg, #5770ff, #4f68f3);
    color: #fff;
    border-color: transparent;
  }
  .testi-stat-label {
    font-size: 14px;
    line-height: 1.45;
    opacity: 0.95;
    margin-bottom: 20px;
    max-width: 240px;
  }
  .testi-stat-value {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(48px, 5.5vw, 72px);
    letter-spacing: 0.01em;
    line-height: 0.9;
    margin-top: auto;
  }
  .testi-stat-caption {
    font-size: 13px;
    margin-top: 4px;
    opacity: 0.9;
  }
  .testi-stars {
    display: flex;
    gap: 3px;
    margin-bottom: 16px;
  }
  .testi-star-icon {
    color: #f59e0b;
    font-size: 16px;
  }
  .testi-quote {
    font-size: 16px;
    font-weight: 300;
    color: var(--ink);
    line-height: 1.7;
    margin-bottom: 24px;
    flex: 1;
    position: relative;
    z-index: 1;
  }
  .testi-quote-mark {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 48px;
    line-height: 0.7;
    color: color-mix(in srgb, var(--accent) 70%, #aab6ff 30%);
    opacity: 0.55;
    margin-bottom: 10px;
  }
  .testi-card--dark .testi-quote,
  .testi-card--dark .testi-name,
  .testi-card--dark .testi-role,
  .testi-card--accent .testi-quote,
  .testi-card--accent .testi-name,
  .testi-card--accent .testi-role {
    color: #fff;
  }
  .testi-card--dark .testi-author,
  .testi-card--accent .testi-author {
    border-top-color: rgba(255,255,255,0.2);
  }
  .testi-author {
    display: flex;
    align-items: center;
    gap: 12px;
    border-top: 1px solid var(--border);
    padding-top: 18px;
  }
  .testi-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }
  .testi-name {
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
  }
  .testi-role {
    font-size: 12px;
    font-weight: 300;
    color: var(--muted);
    margin-top: 2px;
  }

  @media (max-width: 820px) {
    .testi-grid { grid-template-columns: 1fr; }
    .testimonials { padding: 60px 24px; }
    .testi-sub { text-align: left; }
  }

  /* ── FAQ ── */
  .faq {
    padding: 100px 48px;
    max-width: 1280px;
    margin: 0 auto;
  }
  .faq-grid {
    display: grid;
    grid-template-columns: minmax(280px, 0.42fr) minmax(0, 1fr);
    gap: 56px;
    align-items: start;
  }
  .faq-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(44px, 5.6vw, 76px);
    line-height: 0.92;
    letter-spacing: 0.02em;
    color: var(--ink);
  }
  .faq-desc {
    margin-top: 18px;
    font-size: 15px;
    font-weight: 300;
    line-height: 1.7;
    color: var(--muted);
    max-width: 320px;
  }
  .faq-list {
    border-top: 1px solid var(--border);
  }
  .faq-item {
    border-bottom: 1px solid var(--border);
  }
  .faq-btn {
    width: 100%;
    padding: 18px 0;
    background: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    text-align: left;
  }
  .faq-q {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(22px, 2.15vw, 34px);
    line-height: 1;
    letter-spacing: 0.03em;
    color: var(--ink);
  }
  .faq-chevron {
    flex-shrink: 0;
    font-size: 15px;
    color: var(--muted);
    line-height: 1;
    margin-top: 8px;
  }
  .faq-body {
    overflow: hidden;
  }
  .faq-answer {
    max-width: 760px;
    padding: 0 0 18px;
    font-size: 15px;
    font-weight: 300;
    line-height: 1.7;
    color: var(--muted);
  }
  @media (max-width: 900px) {
    .faq {
      padding: 60px 24px;
    }
    .faq-grid {
      grid-template-columns: 1fr;
      gap: 32px;
    }
  }

  /* ── BLOG ── */
  .blog {
    padding: 100px 48px;
    max-width: 1280px;
    margin: 0 auto;
    border-top: 1px solid var(--border);
  }
  .blog-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 56px;
    gap: 24px;
    flex-wrap: wrap;
  }
  .blog-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(44px, 5.5vw, 72px);
    line-height: 0.95;
    letter-spacing: 0.02em;
    color: var(--ink);
  }
  .blog-all-btn {
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: var(--ink);
    background: transparent;
    border: 1.5px solid var(--border);
    border-radius: 999px;
    padding: 10px 22px;
    cursor: pointer;
    transition: background 0.2s, color 0.2s, border-color 0.2s;
    white-space: nowrap;
  }
  .blog-all-btn:hover { background: var(--ink); color: var(--bg); border-color: var(--ink); }
  .blog-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
  .blog-card {
    border-radius: 20px;
    overflow: hidden;
    background: var(--card);
    border: 1px solid var(--border);
    cursor: pointer;
  }
  .blog-card-img {
    width: 100%;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
    transition: transform 0.4s ease;
  }
  .blog-card-img--contain {
    object-fit: contain;
    object-position: center;
    background: #e7e2d9;
  }
  .blog-card:hover .blog-card-img { transform: scale(1.04); }
  .blog-card-body {
    padding: 20px 22px 24px;
  }
  .blog-tag {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 10px;
  }
  .blog-title {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 22px;
    letter-spacing: 0.03em;
    line-height: 1.15;
    color: var(--ink);
    margin-bottom: 10px;
  }
  .blog-excerpt {
    font-size: 13px;
    font-weight: 300;
    color: var(--muted);
    line-height: 1.6;
  }
  .blog-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--border);
  }
  .blog-date { font-size: 12px; color: var(--muted); }
  .blog-arrow {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1.5px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: var(--ink);
    transition: background 0.2s, border-color 0.2s, color 0.2s;
    flex-shrink: 0;
  }
  .blog-card:hover .blog-arrow { background: var(--ink); color: var(--bg); border-color: var(--ink); }

  @media (max-width: 820px) {
    .blog { padding: 60px 24px; }
    .blog-grid { grid-template-columns: 1fr; }
  }

  /* ── BLUE FOOTER ── */
  .cta-footer {
    margin-top: 36px;
    background: #4f68f3;
    color: #fff;
    padding: 30px 48px 20px;
  }
  .scroll-toggle-wrap {
    position: fixed;
    left: 50%;
    bottom: 14px;
    transform: translate3d(-50%, 0, 0);
    z-index: 260;
    pointer-events: none;
    width: max-content;
  }
  .scroll-toggle-wrap > * {
    pointer-events: auto;
  }
  .scroll-toggle-inner {
    position: static;
    left: auto;
    bottom: auto;
    transform: none;
    z-index: auto;
  }
  .toggle-track.scroll-style {
    width: 30px;
    height: 14px;
    border-radius: 999px;
    background: #4f68f3;
    border: none;
    box-shadow: 0 4px 10px rgba(79, 104, 243, 0.35);
  }
  .toggle-track.scroll-style.on {
    background: #3f58ea;
    border: none;
  }
  .toggle-track.scroll-style .toggle-thumb {
    width: 10px;
    height: 10px;
    top: 2px;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
  }
  .footer-top {
    max-width: 1280px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
    align-items: start;
  }
  .footer-top-label {
    font-size: 12px;
    font-weight: 400;
    opacity: 0.9;
    margin-bottom: 6px;
  }
  .footer-top-value {
    font-size: clamp(24px, 2.4vw, 34px);
    font-family: 'Bebas Neue', sans-serif;
    letter-spacing: 0.02em;
    line-height: 1;
  }
  .footer-socials {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .footer-social-btn {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.7);
    background: transparent;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    cursor: pointer;
  }
  .footer-divider {
    max-width: 1280px;
    margin: 16px auto 0;
    border-top: 1px solid rgba(255,255,255,0.5);
  }
  .footer-bottom {
    max-width: 1280px;
    margin: 14px auto 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    color: rgba(255,255,255,0.95);
  }
  .footer-copy { font-size: 14px; }
  .footer-created {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
  }
  .footer-created-avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    overflow: hidden;
    display: inline-block;
    border: 1px solid rgba(255,255,255,0.45);
  }
  .footer-created-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  @media (max-width: 820px) {
    .cta-footer { padding: 22px 24px 16px; }
    .footer-top { grid-template-columns: 1fr; gap: 16px; }
    .footer-bottom { flex-direction: column; align-items: flex-start; }
    .scroll-toggle-wrap { bottom: 12px; }
  }

`;

// ─── COMPONENT ───────────────────────────────────────────────────────────────

export default function Portfolio() {
  const [dark, setDark] = useState(false);
  const [openAcc, setOpenAcc] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [hoveredSvc, setHoveredSvc] = useState(null);
  const [heroTilt, setHeroTilt] = useState({ x: 0, y: 0 });
  const [showScrollToggle, setShowScrollToggle] = useState(false);
  const [brandIndex, setBrandIndex] = useState(0);

  // Cursor spring
  const cursorX = useSpring(0, { stiffness: 300, damping: 30 });
  const cursorY = useSpring(0, { stiffness: 300, damping: 30 });

  // Parallax
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [400, 1200], [0, -50]);

  // Hero photo card scroll transforms
  const cardRotate = useTransform(scrollY, [0, 400], [6, 0]);
  const cardScale = useTransform(scrollY, [0, 400], [0.92, 1]);
  const cardY = useTransform(scrollY, [0, 400], [0, -30]);
  const smoothRotate = useSpring(cardRotate, { stiffness: 80, damping: 20 });
  const smoothScale = useSpring(cardScale, { stiffness: 80, damping: 20 });
  const smoothY = useSpring(cardY, { stiffness: 80, damping: 20 });

  // InView refs
  const svcRef = useRef(null);
  const rightImgRef = useRef(null);
  const rightImgInView = useInView(rightImgRef, { once: true, margin: '-80px' });
  const aboutRef = useRef(null);
  const aboutInView = useInView(aboutRef, { once: true, margin: '-80px' });
  const featuredRef = useRef(null);
  const svcHeadingRef = useRef(null);
  const svcHeadingInView = useInView(svcHeadingRef, { once: true, margin: '-80px' });

  const scrollStagger = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };
  const scrollGroup = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.12, delayChildren: 0.04 },
    },
  };

  /* Hero badge: 1s empty → hand + 2 hello shakes → HI 1s → repeat */
  const [heroBadgePhase, setHeroBadgePhase] = useState('wait');
  const WAVE_SHAKE_MS = 2300;
  useEffect(() => {
    let t;
    if (heroBadgePhase === 'wait') {
      t = window.setTimeout(() => setHeroBadgePhase('wave'), 1000);
    } else if (heroBadgePhase === 'wave') {
      t = window.setTimeout(() => setHeroBadgePhase('hi'), WAVE_SHAKE_MS);
    } else if (heroBadgePhase === 'hi') {
      t = window.setTimeout(() => setHeroBadgePhase('wait'), 1000);
    }
    return () => window.clearTimeout(t);
  }, [heroBadgePhase]);

  // Mouse tracker for cursor follower
  useEffect(() => {
    const onMove = (e) => {
      cursorX.set(e.clientX + 24);
      cursorY.set(e.clientY - 60);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [cursorX, cursorY]);

  useEffect(() => {
    const onScroll = () => {
      setShowScrollToggle(window.scrollY > Math.max(520, window.innerHeight * 0.95));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setBrandIndex((prev) => (prev + 1) % BRAND_HIGHLIGHTS.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, []);

  const activeBrand = BRAND_HIGHLIGHTS[brandIndex];

  const handleHeroMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    setHeroTilt({ x: py * -6, y: px * 8 });
  };

  return (
    <div className={`portfolio${dark ? ' dark' : ''}`}>
      <style>{CSS}</style>
      <div className="bg-blue-balls" aria-hidden>
        {Array.from({ length: 8 }).map((_, i) => (
          <span key={i} className="bg-blue-ball" />
        ))}
      </div>

      {/* ══ NAV ══ */}
      <motion.div
        className="nav-outer"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      >
        <div className="nav-pill">
          <img
            className="nav-avatar"
            src="/N7GRY.jpg"
            alt="Duncan Robert"
          />
          {NAV_LINKS.map((l, i) => (
            <motion.button
              key={l}
              className="nav-link"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 * i + 0.3 }}
            >
              {l}
            </motion.button>
          ))}
          <div className="nav-sep" />
          <button className="nav-cta">Contact</button>
        </div>
      </motion.div>

      {/* ══ HERO ══ */}
      <section className="hero">
        {/* 3-col row: name + DIGITAL | portrait | DESIGNER + bio (reference: text never sits over the image) */}
        <div className="hero-row">
          <motion.p
            className="hero-label hero-label--left hero-name"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            style={{ color: 'var(--ink)', fontWeight: 500, fontSize: 13, letterSpacing: '0.14em', textTransform: 'uppercase' }}
          >
            SAMI AL
          </motion.p>

          <motion.span
            className="hero-word hero-digital"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.75, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            HARBI
          </motion.span>

          {/* Photo card — spans all grid rows, vertically centered */}
          <motion.div
            className="card-wrap hero-card"
            initial={{ scale: 0.86, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.75, delay: 0.05, ease: 'easeOut' }}
            whileHover={{ scale: 1.05 }}
            onMouseMove={handleHeroMouseMove}
            onMouseLeave={() => setHeroTilt({ x: 0, y: 0 })}
          >
            <motion.div
              className="photo-card"
              style={{
                rotateX: heroTilt.x,
                rotateY: heroTilt.y,
                transformStyle: 'preserve-3d',
                rotate: smoothRotate,
                scale: smoothScale,
                y: smoothY,
              }}
              animate={{
                y: [0, -10, 0],
                rotateX: [heroTilt.x, heroTilt.x + 2, heroTilt.x],
                rotateY: [heroTilt.y, heroTilt.y + 7, heroTilt.y],
                rotateZ: [0, 1.2, 0, -1.2, 0],
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <img
                src="/N7GRY.jpg"
                alt="Designer portrait"
              />
            </motion.div>

            {/* Corner badge: shell = center on image corner → ~25% circle inside, ~75% outside */}
            <div className="wave-btn-shell">
              <motion.div className="wave-btn" aria-live="polite">
                <AnimatePresence mode="wait">
                  {heroBadgePhase === 'wait' ? (
                    <motion.span
                      key="wait"
                      className="wave-btn-phase-placeholder"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 0 }}
                      exit={{ opacity: 0 }}
                      aria-hidden
                    />
                  ) : heroBadgePhase === 'wave' ? (
                    <motion.span
                      key="wave"
                      className="wave-btn-wave-inner"
                      initial={{ scale: 0.88, opacity: 0 }}
                      animate={{
                        scale: 1,
                        opacity: 1,
                        rotate: [0, -14, 11, -13, 10, -12, 9, 0],
                      }}
                      exit={{ scale: 0.9, opacity: 0, transition: { duration: 0.2 } }}
                      transition={{
                        rotate: {
                          duration: 1,
                          repeat: 1,
                          ease: 'easeInOut',
                        },
                        scale: { duration: 0.28, ease: 'easeOut' },
                        opacity: { duration: 0.25 },
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: '100%',
                        transformOrigin: '50% 92%',
                      }}
                    >
                      <svg
                        className="wave-btn-hand-svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <path
                          stroke="#fff"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M10.05 4.575a1.575 1.575 0 1 0-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 0 1 3.15 0v1.5m-3.15 0 .075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 0 1 3.15 0V15M6.9 7.575a1.575 1.575 0 1 0-3.15 0v8.175a6.75 6.75 0 0 0 6.75 6.75h2.018a5.25 5.25 0 0 0 3.712-1.538l1.732-1.732a5.25 5.25 0 0 0 1.538-3.712l.003-2.024a.668.668 0 0 1 .198-.471 1.575 1.575 0 1 0-2.228-2.228 3.818 3.818 0 0 0-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0 1 16.35 15m.002 0h-.002"
                        />
                      </svg>
                    </motion.span>
                  ) : (
                    <motion.span
                      key="hi"
                      className="hi-text"
                      initial={{ scale: 0.82, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.88, opacity: 0, transition: { duration: 0.2 } }}
                      transition={{ type: 'spring', stiffness: 380, damping: 24 }}
                    >
                      HI!
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>

          </motion.div>

          <motion.div
            className="hero-right-stack"
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.75, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <span className="hero-word hero-designer">Chairman </span>
            <motion.div
              className="hero-sub-wrap"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <p className="hero-sub-desc">
                Hospitality, Aviation & Travel Group Leader
              </p>
            </motion.div>
          </motion.div>
        </div>

      </section>

      {/* ══ SERVICES ══ */}
      <section className="services" ref={svcRef}>
        <div className="svc-grid">
          {/* Left column */}
          <motion.div
            variants={scrollGroup}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <h2 className="svc-heading" ref={svcHeadingRef} style={{ display: 'flex', flexWrap: 'wrap', gap: '0 0.22em' }}>
              {'GROUP OVERVIEW'.split(' ').map((word, i) => (
                <span key={i} style={{ overflow: 'hidden', display: 'inline-block' }}>
                  <motion.span
                    style={{ display: 'inline-block' }}
                    initial={{ y: '100%' }}
                    animate={svcHeadingInView ? { y: '0%' } : {}}
                    transition={{ delay: i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {word}
                  </motion.span>
                </span>
              ))}
            </h2>

            <motion.p
              className="svc-sub"
              variants={scrollStagger}
            >
              Manazil Al Mokhtara Group operates across multiple sectors:
            </motion.p>

            {/* Accordion */}
            <motion.div
              className="accordion"
              variants={scrollStagger}
            >
              {SERVICES.map((s, i) => (
                <motion.div
                  key={s.id}
                  className="acc-item"
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, ease: 'easeOut', delay: i * 0.15 }}
                  whileHover={{ y: -8, boxShadow: '0 20px 60px rgba(255,255,255,0.05)' }}
                >
                  <button
                    className="acc-row"
                    onClick={() => setOpenAcc(openAcc === i ? null : i)}
                    onMouseEnter={() => setHoveredSvc(i)}
                    onMouseLeave={() => setHoveredSvc(null)}
                  >
                    <span className={`acc-num${openAcc === i ? ' on' : ''}`}>{s.id}.</span>
                    <span className={`acc-label${openAcc === i ? ' on' : ''}`}>{s.label}</span>
                    <motion.span
                      className="acc-chevron"
                      animate={{ rotate: openAcc === i ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      ⌃
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false}>
                    {openAcc === i && (
                      <motion.div
                        className="acc-body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: 'easeInOut' }}
                      >
                        <p className="acc-desc">{s.desc}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right column — sticky + parallax */}
          <div className="svc-right">
            <motion.div
              ref={rightImgRef}
              initial={{ x: 60, rotate: 6, opacity: 0 }}
              animate={rightImgInView ? { x: 0, rotate: 3, opacity: 1 } : {}}
              transition={{ duration: 0.9, ease: 'easeOut' }}
              style={{ y: parallaxY }}
            >
              <img
                src="https://images.unsplash.com/photo-1561070791-2526d30994b5?w=600&h=700&fit=crop"
                alt="Design workspace"
                className="right-img"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══ ABOUT ME ══ */}
      <section className="about" ref={aboutRef} aria-labelledby="about-heading">
        <div className="about-grid">
          <div className="about-left">
            <motion.h2
              id="about-heading"
              className="about-heading"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              ABOUT ME
            </motion.h2>

            <motion.p
              className="about-desc"
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: 0.1, ease: 'easeOut' }}
            >
              Sami Al Harbi is the Chairman of Manazil Al Mokhtara Group, a leading hospitality and travel services group in Saudi Arabia. The group operates a diversified portfolio including hotels, transportation, catering, aviation, and travel services.
            </motion.p>
            <motion.p
              className="about-desc about-desc-second"
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65, delay: 0.16, ease: 'easeOut' }}
            >
              The company has grown from a single hotel in Al Madinah into a large-scale operation managing more than 17 hotels with over 4,000 rooms across major cities including Madinah, Makkah, and Jeddah.
            </motion.p>

            <div className="about-stats" role="list">
              <AboutStatBlock
                target={22}
                label="Years in Business"
                motionDelay={0.15}
                countDelayMs={150}
                inView={aboutInView}
              />
              <AboutStatBlock
                target={17}
                label="Hotels"
                motionDelay={0.25}
                countDelayMs={250}
                inView={aboutInView}
              />
              <AboutStatBlock
                target={60}
                label="Guests Served Monthly"
                suffix="k+"
                motionDelay={0.35}
                countDelayMs={350}
                inView={aboutInView}
              />
            </div>

            <motion.div
              className="about-contact"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: 0.3, ease: 'easeOut' }}
            >
              <div>
                <div className="about-contact-label">Email :</div>
                <div className="about-contact-val">designer@example.com</div>
              </div>
            </motion.div>

            <div className="about-social">
              {[
                { key: 'x', label: 'X', content: <span style={{ fontSize: 17 }}>𝕏</span> },
                {
                  key: 'ig',
                  label: 'Instagram',
                  content: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      />
                      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" />
                      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" />
                    </svg>
                  ),
                },
                {
                  key: 'li',
                  label: 'LinkedIn',
                  content: (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                      <rect x="4" y="4" width="16" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
                      <circle cx="8.1" cy="8.1" r="1.1" fill="currentColor" />
                      <path d="M7.2 10.7v6.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                      <path
                        d="M10.9 16.9v-3.2c0-1.2.8-2 1.9-2 1.1 0 1.8.7 1.8 2v3.2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  ),
                },
              ].map((item, i) => (
                <motion.button
                  key={item.key}
                  type="button"
                  className="about-soc-btn"
                  aria-label={item.label}
                  initial={{ scale: 0, opacity: 0 }}
                  animate={aboutInView ? { scale: 1, opacity: 1 } : {}}
                  transition={{
                    type: 'spring',
                    stiffness: 380,
                    damping: 22,
                    delay: i * 0.07,
                  }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.92 }}
                >
                  {item.content}
                </motion.button>
              ))}
            </div>

            {/* <motion.button
              type="button"
              className="about-story-btn"
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.45, ease: 'easeOut' }}
              whileHover={{ scale: 1.05, transition: { duration: 0.22, ease: 'easeOut' } }}
              whileTap={{ scale: 0.95, transition: { duration: 0.22, ease: 'easeOut' } }}
            >
              MY STORY
            </motion.button> */}
          </div>

          <motion.div
            className="about-photo-outer"
            initial={{ x: 60, opacity: 0, rotate: 2 }}
            whileInView={{ x: 0, opacity: 1, rotate: 2 }}
            viewport={{ once: true }}
            whileHover={{
              rotate: 0,
              scale: 1.02,
              transition: { duration: 0.25, ease: 'easeOut' },
            }}
            transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
          >
            <div className="about-photo-card">
              <img
                src="/N7GRY.jpg"
                alt="Duncan portrait"
              />
            </div>
            <motion.div
              className="about-pulse-dot"
              animate={{
                scale: [1, 1.65, 1],
                boxShadow: [
                  '0 0 0 0px rgba(61,76,245,0.55)',
                  '0 0 0 10px rgba(61,76,245,0)',
                  '0 0 0 0px rgba(61,76,245,0)',
                ],
              }}
              transition={{ repeat: Infinity, duration: 2.2, ease: 'easeInOut' }}
            />
          </motion.div>
        </div>
      </section>

      <FeaturedStackingShowcase
        ref={featuredRef}
        dark={dark}
        items={FEATURED_PROJECTS.map((p) => ({
          id: p.id,
          imageUrl: p.img,
          title: p.title,
          description: p.desc,
          tag: p.category,
          badge: p.badge,
          imageFit: p.imageFit,
          imagePosition: p.imagePosition,
        }))}
        intro="These selected projects reflect my passion for blending strategy with creativity — solving real problems through thoughtful design and impactful storytelling."
      />

      {/* ══ TESTIMONIALS ══ */}
      <section className="testimonials">
        <div className="testi-header">
          <motion.h2
            className="testi-heading"
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            Our Achievement
          </motion.h2>
          <motion.p
            className="testi-sub"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Elevating Hospitality to New Heights
          </motion.p>
        </div>

        <motion.div
          className="testi-grid"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.14 } } }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {TESTIMONIALS.map((t) => (
            <motion.div
              key={t.id}
              className={`testi-card ${t.type === 'stat-dark' ? 'testi-card--dark' : t.type === 'stat-accent' ? 'testi-card--accent' : ''
                }`.trim()}
              variants={{
                hidden: { y: 40, opacity: 0 },
                visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } },
              }}
              whileHover={{ y: -6, transition: { duration: 0.25, ease: 'easeOut' } }}
            >
              {t.type === 'review' ? (
                <>
                  <div className="testi-quote-mark">“</div>
                  <p className="testi-quote">{t.quote}</p>
                  <div className="testi-author">
                    <div>
                      <div className="testi-name">{t.name}</div>
                      <div className="testi-role">{t.role}</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="testi-stat-label">{t.label}</p>
                  <div className="testi-stat-value">{t.stat}</div>
                  <div className="testi-stat-caption">{t.statCaption}</div>
                </>
              )}
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ══ FAQ ══ */}
      <section className="faq">
        <div className="faq-grid">
          <div>
            <motion.h2
              className="faq-title"
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
            >
              FREQUENTLY ASKED
              <br />
              QUESTIONS
            </motion.h2>
            <motion.p
              className="faq-desc"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              Here are answers to some of the most common questions I receive as a freelance designer.
              If you don&apos;t see your question here, feel free to reach out — I&apos;m happy to help!
            </motion.p>
          </div>

          <motion.div
            className="faq-list"
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
          >
            {FAQ_ITEMS.map((item, idx) => (
              <motion.div
                key={item.id}
                className="faq-item"
                variants={{
                  hidden: { y: 24, opacity: 0 },
                  visible: { y: 0, opacity: 1, transition: { duration: 0.45, ease: 'easeOut' } },
                }}
              >
                <button
                  type="button"
                  className="faq-btn"
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                >
                  <span className="faq-q">{item.id}. {item.q}</span>
                  <motion.span
                    className="faq-chevron"
                    animate={{ rotate: openFaq === idx ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    ⌃
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {openFaq === idx ? (
                    <motion.div
                      className="faq-body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.28, ease: 'easeInOut' }}
                    >
                      <p className="faq-answer">{item.a}</p>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══ BLOG ══ */}
      <section className="blog">
        <div className="blog-header">
          <motion.h2
            className="blog-heading"
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            INSIGHTS &<br />THOUGHTS
          </motion.h2>
          {/* <motion.button
            type="button"
            className="blog-all-btn"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.25 }}
            whileHover={{ scale: 1.04, transition: { duration: 0.2 } }}
            whileTap={{ scale: 0.96 }}
          >
            All Articles →
          </motion.button> */}
        </div>

        <motion.div
          className="blog-grid"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.13 } } }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {BLOG_POSTS.map((post) => (
            <motion.article
              key={post.id}
              className="blog-card"
              variants={{
                hidden: { y: 40, opacity: 0 },
                visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } },
              }}
              whileHover={{ y: -8, transition: { duration: 0.25, ease: 'easeOut' } }}
            >
              <div style={{ overflow: 'hidden' }}>
                <img
                  className={`blog-card-img${post.fitMode === 'contain' ? ' blog-card-img--contain' : ''}`}
                  src={post.img}
                  alt={post.title}
                />
              </div>
              <div className="blog-card-body">
                <div className="blog-tag">{post.tag}</div>
                <div className="blog-title">{post.title}</div>
                <div className="blog-excerpt">{post.excerpt}</div>
                <div className="blog-meta">
                  <span className="blog-date">{post.date}</span>
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </section>

      {/* ══ CTA / FOOTER ══ */}
      <section className="cta-footer">
        <div className="footer-top">
          <div>
            <div className="footer-top-label">Email :</div>
            <div className="footer-top-value">designer@example.com</div>
          </div>
          <div>
            <div className="footer-top-label">Call Today :</div>
            <div className="footer-top-value">+1 (555) 123-4567</div>
          </div>
          <div>
            <div className="footer-top-label">Social :</div>
            <div className="footer-socials">
              {['X', '◉', 'Be', '◌'].map((s, i) => (
                <button key={`${s}-${i}`} type="button" className="footer-social-btn">{s}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="footer-divider" />

        <div className="footer-bottom">
          <span className="footer-copy">© 2025 Manazil Al Mokhtara Group. All rights reserved.</span>
        </div>
      </section>

      {/* ══ FLOATING BADGE (after hero scroll) ══ */}
      <AnimatePresence>
        {showScrollToggle ? (
          <motion.div
            className="f-badge"
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.95 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
          >
            <div className="f-badge-tag">{activeBrand.name}</div>
            <div className="f-badge-thumb">
              <img
                src={activeBrand.image}
                alt={activeBrand.name}
              />
            </div>
            <div className="f-badge-actions">
              <a className="f-btn primary" href={activeBrand.link} target="_blank" rel="noreferrer">
                Visit
              </a>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* ══ CURSOR IMAGE FOLLOWER ══ */}
      <AnimatePresence>
        {hoveredSvc !== null && (
          <motion.img
            key={hoveredSvc}
            src={SERVICES[hoveredSvc].img}
            alt=""
            className="cursor-img"
            style={{ x: cursorX, y: cursorY }}
            initial={{ scale: 0.8, opacity: 0, rotate: -4 }}
            animate={{ scale: 1, opacity: 1, rotate: 3 }}
            exit={{ scale: 0.8, opacity: 0, rotate: -4 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>

      {/* ══ SCROLL TOGGLE (appears after scrolling) ══ */}
      <AnimatePresence>
        {showScrollToggle ? (
          <motion.div
            className="scroll-toggle-wrap"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.2 }}
          >
            <div className="toggle-wrap scroll-toggle-inner">
              <div
                className={`toggle-track scroll-style${dark ? ' on' : ''}`}
                onClick={() => setDark((d) => !d)}
                aria-label="Toggle light mode"
                role="switch"
                aria-checked={dark}
              >
                <motion.div
                  className="toggle-thumb"
                  animate={{ x: dark ? 18 : 2 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                />
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

